/**
 * StartStopCard — the Engine screen's 4 live start/stop switches
 * (WSRPDM-0014, contract §2.1/§2.2). Registry-driven off `ENABLE_FLAGS` —
 * adding a 5th flag later needs no JSX change here.
 *
 * Each toggle PUTs ONLY that one flag (`{ [key]: checked }`) — a `null` flag
 * is left unchanged per §2.2, and the other 3 flags + all 28 knobs are
 * omitted entirely, so this never touches them. The PUT's re-read response
 * becomes the new baseline via `onSaved`.
 *
 * `globalEnabled` is shown read-only (contract: "read on any row's
 * `globalEnabled`" — no standalone GET, so this screen's own GET response
 * carries the current value) — it is NEVER toggled here; that's the Tenants
 * screen's master switch (contract §1.3).
 */
import { useState } from 'react';
import { Alert, Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { AppSwitch, HelpPopover } from '@/components/ui';
import { putConfig } from '@/api/wsr/scheduleConfig';
import {
  ENABLE_FLAGS,
  ENGINE_HELP_SCOPE_FALLBACK,
  type EnableFlagKey,
} from '@/domain/scheduleKnobs';
import type { PlatformScheduleConfigView } from '@/api/wsr/types';

interface StartStopCardProps {
  config: PlatformScheduleConfigView;
  onSaved: (view: PlatformScheduleConfigView) => void;
}

export function StartStopCard({ config, onSaved }: StartStopCardProps) {
  const { t } = useCopy();
  const [togglingKey, setTogglingKey] = useState<EnableFlagKey | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleToggle = async (key: EnableFlagKey, checked: boolean) => {
    setError(null);
    setTogglingKey(key);
    try {
      const view = await putConfig({ [key]: checked });
      onSaved(view);
    } catch {
      setError(t('engine.flags.toggleError', 'Could not update. Please try again.'));
    } finally {
      setTogglingKey(null);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6" component="h2">
          {t('engine.flags.title', 'Start / stop')}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('engine.flags.globalReadOnly', 'Global (read-only — see Tenants)')}
          </Typography>
          <Chip
            size="small"
            label={
              config.globalEnabled
                ? t('global.toggleEnabled', 'Enabled')
                : t('global.toggleDisabled', 'Disabled')
            }
            color={config.globalEnabled ? 'success' : 'default'}
            variant={config.globalEnabled ? 'filled' : 'outlined'}
          />
        </Stack>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <Stack spacing={2}>
        {ENABLE_FLAGS.map((flag) => {
          const label = t(flag.labelKey, flag.fallbackLabel);
          const detail = t(`engine.fields.${flag.key}.detail`, '');
          return (
            <Box key={flag.key}>
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                <AppSwitch
                  label={label}
                  checked={config[flag.key]}
                  onChange={(checked) => void handleToggle(flag.key, checked)}
                  disabled={togglingKey !== null}
                />
                {detail ? (
                  <HelpPopover
                    label={label}
                    scope={t(
                      `help.scope.${flag.helpScope}`,
                      ENGINE_HELP_SCOPE_FALLBACK[flag.helpScope],
                    )}
                    body={detail}
                  />
                ) : null}
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4.5 }}>
                {t(flag.descriptionKey, flag.fallbackDescription)}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}
